<template>
  <OrderItemBlock
    :imageUrl="imageUrl"
    :imageAlt="`${groupName} - ${color}`"
    :name="groupName"
    :descriptionLines="descriptionLines"
    :sizeModifier="sizeModifier"
    :quantity="quantity"
    :isQuantityVisible="!isGiftCard"
    :hasReachedMaxQuantity="hasReachedMaxQuantity"
    :price="price"
    :productPath="contextualProductPath"
    :isOutOfStock="outOfStock"
    :isMerging="isMerging"
    :isTransitioning="isTransitioning"
    :isReadOnly="isReadOnly"
    :isHidden="isHidden"
    :isRemoved="isRemoved"
    :extraDescriptionLine="giftCardMessage"
    :isTruncatingExtraDescriptionLine="!isShowingFullGiftCardMsg"
    :contextMessage="isLowStock ? $tPlural('pdp.purchase_pod.low_stock', stock) : ''"
    :isBackorderable="isBackorderable"
    v-data-test-id="'regular-order-item'"
    @remove-item="removeItem"
    @change-quantity="changeQuantity"
    @out-of-stock-click="showOutOfStockNotification"
  />
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { PRODUCT_TYPES } from '@/config/constants'
import { getSlugFromGroupName } from '@/helpers/url.js'
import { isLowStock } from '@/stores/product'
import {
  notifyExternalServices,
  ACTIONS,
} from '@/services/external-communication/external-communication'
import OrderItemBlock, { SIZE_MODIFIERS } from './OrderItemBlock.vue'

export { SIZE_MODIFIERS }

export default {
  name: 'RegularOrderItem',
  components: {
    OrderItemBlock,
  },

  props: {
    id: { type: Number, required: true },
    variantId: { type: Number, required: true },
    imageUrl: { type: String, required: true },
    productPath: { type: String, default: '#' },
    productType: { type: String, default: '' },
    sku: { type: String, default: '' },
    variantSku: { type: String, default: '' },
    groupName: { type: String, default: '' },
    color: { type: String, default: '' },
    size: { type: String },
    price: { type: Number },
    quantity: { type: Number, default: 1, required: true },
    viewmode: { type: String, default: '' },
    lineItemId: { type: Number },
    stock: { type: Number, default: 0 },
    isStockIgnored: { type: Boolean, default: false },
    isMerging: { type: Boolean },
    isTransitioning: { type: Boolean },
    isHidden: { type: Boolean },
    isRemoved: { type: Boolean },
    isReadOnly: { type: Boolean },
    sizeModifier: { type: String },
    flashContext: { type: String },
    giftCardMessage: { type: String },
    isBackorderable: { type: Boolean, default: false },
    isShowingFullGiftCardMsg: { type: Boolean, default: false },
  },

  computed: {
    ...mapState('store', ['currency']),

    ...mapGetters({
      viewportWidth: 'viewport/width',
    }),

    descriptionLines() {
      const lines = [this.size]
      if (!this.isGiftCard) lines.unshift(this.color)
      return lines
    },

    isGiftCard() {
      return this.productType === PRODUCT_TYPES.giftCards
    },

    hasReachedMaxQuantity() {
      return this.quantity === this.stock || this.outOfStock
    },

    outOfStock() {
      return (this.quantity > this.stock || !this.stock) && !this.isStockIgnored
    },

    contextualProductPath() {
      return this.outOfStock ? 'javascript:;' : this.productPath
    },

    isLowStock() {
      return isLowStock(this.stock) && !this.isStockIgnored
    },
  },

  watch: {
    outOfStock: {
      immediate: true,
      handler(value) {
        if (value) this.preloadOutOfStockOverlay()
      },
    },
  },

  methods: {
    ...mapActions({
      showOutOfStockOverlay: 'overlay/showOutOfStockOverlay',
      preloadOutOfStockOverlay: 'overlay/preloadOutOfStockOverlay',
      updateCartItem: 'order/updateCartItem',
      addMessage: 'flash/addMessage',
    }),

    removeItem(element) {
      this.$emit('remove-item', element)

      let payload = { skuId: this.id, quantity: 0, increase: this.quantity * -1 }
      if (this.isGiftCard) payload.lineItemId = this.lineItemId
      this.updateCartItem(payload)
      this.trackOrderChange(payload, true)
    },

    changeQuantity(increase) {
      if (this.hasReachedMaxQuantity && increase > 0) return
      // TODO: Add flash message to navigation?
      // return window.On.Store.dispatch('flash/addMessage', {
      //   text: this.$t('minicart.maximum_stock'),
      //   type: MESSAGE_TYPES.warning,
      // })

      const newQuantity = this.quantity + increase
      const onError = message => {
        this.addMessage({
          text: message,
          type: 'error',
          context: this.flashContext,
        })
      }
      let payload = { skuId: this.id, quantity: newQuantity, increase, onError }
      newQuantity <= 0 ? this.removeItem(payload) : this.updateQuantity(payload)
    },

    updateQuantity(payload) {
      this.updateCartItem(payload)
      this.trackOrderChange(payload)
    },

    trackOrderChange(payload, isCompleteRemoval = false) {
      const action = payload.increase > 0 ? ACTIONS.addToCart : ACTIONS.removeFromCart
      const quantity = isCompleteRemoval ? this.quantity : 1
      const { variantId, productSku, sku, size, price, currency, viewmode } = this

      notifyExternalServices({
        action,
        product: { variantId, productSku, sku, size, quantity, price, currency, viewmode },
      })
    },

    showOutOfStockNotification() {
      const { variantSku: sku, isBackorderable, imageUrl, groupName } = this
      this.showOutOfStockOverlay({
        sku,
        slug: getSlugFromGroupName(groupName),
        imageUrl,
        isBackorderable,
      })
    },
  },
}
</script>
